import React, { createContext, useContext, useState, useEffect } from 'react';
import settingsService from '../services/settingsService';
import subdomainService from '../services/subdomainService';
import { getCurrentSubdomain, isSubdomainEnvironment } from '../utils/subdomainUtils';

type CompanyContextType = {
  name: string;
  setName: (name: string) => void;
  address: string;
  setAddress: (address: string) => void;
  logo: string;
  setLogo: (logo: string) => void;
  companyId?: number;
  setCompanyId: (id: number) => void;
};

const CompanyContext = createContext<CompanyContextType>({
  name: '',
  setName: () => {},
  address: '',
  setAddress: () => {},
  logo: '',
  setLogo: () => {},
  companyId: undefined,
  setCompanyId: () => {}
});

export const useCompany = () => useContext(CompanyContext);

export const CompanyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [name, setName] = useState('Invoice & Cloud Management');
  const [address, setAddress] = useState('');
  const [logo, setLogo] = useState('');
  const [companyId, setCompanyId] = useState<number | undefined>(undefined);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        // Check if we're in a subdomain environment
        if (isSubdomainEnvironment()) {
          const subdomain = getCurrentSubdomain();
          if (subdomain) {
            const company = await subdomainService.getCompanyBySubdomain(subdomain);
            if (company) {
              setName(company.name);
              setAddress(company.address || '');
              setCompanyId(company.id);
              setLogo(company.logo_url || '');
              return;
            }
          }
        }
        
        // Fallback to settings-based company info
        const data = await settingsService.getCompanyInfo();
        setName(data?.name?.trim() ? data.name : 'Invoice & Cloud Management');
        setAddress(data?.address?.trim() ? data.address : '');
        setLogo(data?.logo || '');
      } catch (error) {
        console.error('Error fetching company info:', error);
        setName('Invoice Management');
      }
    };
    fetchCompanyInfo();
  }, []);

  return (
    <CompanyContext.Provider value={{ name, setName, address, setAddress, logo, setLogo, companyId, setCompanyId }}>
      {children}
    </CompanyContext.Provider>
  );
};
